import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Play, Square, Save, Trash2, Route, MousePointer, Eraser } from 'lucide-react';

const W = 900, H = 560;
const FIELD_LINES = [40, 130, 260, 450, 640, 770, 860]; // try, 22m, 10m, half, 10m, 22m, try

let idSeq = 1;
const nid = () => `m${Date.now().toString(36)}${idSeq++}`;

export default function Playbook() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [markers, setMarkers] = useState([]);
  const [paths, setPaths] = useState({});
  const [mode, setMode] = useState('move'); // move | path
  const [selected, setSelected] = useState(null);
  const [plays, setPlays] = useState([]);
  const [playName, setPlayName] = useState('');
  const [phase, setPhase] = useState('attack');
  const [currentPlayId, setCurrentPlayId] = useState(null);
  const [animating, setAnimating] = useState(false);
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const animRef = useRef(null);
  const baseRef = useRef(null);

  const loadPlays = useCallback(async () => {
    try {
      const res = await api.get('/playbook/plays');
      setPlays(res.data);
    } catch (e) { console.warn('Playbook load blocked or failed', e?.response?.status); }
  }, []);
  useEffect(() => { loadPlays(); }, [loadPlays]);

  const svgPoint = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return [
      Math.max(15, Math.min(W - 15, ((e.clientX - rect.left) / rect.width) * W)),
      Math.max(15, Math.min(H - 15, ((e.clientY - rect.top) / rect.height) * H)),
    ];
  };

  const addMarker = (team) => {
    const count = markers.filter((m) => m.team === team).length;
    if (team === 'ball' && markers.some((m) => m.team === 'ball')) { toast.info('Only one ball on the board'); return; }
    setMarkers((prev) => [...prev, {
      id: nid(), team,
      x: team === 'attack' ? 200 : team === 'defense' ? 700 : 450,
      y: 80 + (count % 10) * 45,
      label: team === 'ball' ? '' : String(count + 1),
    }]);
  };

  const onMarkerDown = (e, m) => {
    e.stopPropagation();
    setSelected(m.id);
    if (mode === 'move' && !animating) dragRef.current = m.id;
  };
  const onMove = (e) => {
    if (!dragRef.current) return;
    const [x, y] = svgPoint(e);
    setMarkers((prev) => prev.map((m) => (m.id === dragRef.current ? { ...m, x, y } : m)));
  };
  const onUp = () => { dragRef.current = null; };
  const onFieldClick = (e) => {
    if (mode !== 'path' || !selected || animating) return;
    const [x, y] = svgPoint(e);
    setPaths((prev) => ({ ...prev, [selected]: [...(prev[selected] || []), [x, y]] }));
  };

  const clearPaths = () => setPaths({});
  const clearBoard = () => { setMarkers([]); setPaths({}); setSelected(null); setCurrentPlayId(null); setPlayName(''); };
  const removeSelected = () => {
    if (!selected) return;
    setMarkers((prev) => prev.filter((m) => m.id !== selected));
    setPaths((prev) => { const p = { ...prev }; delete p[selected]; return p; });
    setSelected(null);
  };

  const animate = () => {
    if (animating) { cancelAnimationFrame(animRef.current); setMarkers(baseRef.current); setAnimating(false); return; }
    if (!Object.keys(paths).length) { toast.info('Draw at least one run line first (Path mode)'); return; }
    baseRef.current = markers;
    setAnimating(true);
    const start = performance.now();
    const dur = 3000;
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      setMarkers(baseRef.current.map((m) => {
        const pts = [[m.x, m.y], ...(paths[m.id] || [])];
        if (pts.length < 2) return m;
        const total = pts.length - 1;
        const seg = Math.min(Math.floor(t * total), total - 1);
        const local = t * total - seg;
        const [x1, y1] = pts[seg];
        const [x2, y2] = pts[seg + 1];
        return { ...m, x: x1 + (x2 - x1) * local, y: y1 + (y2 - y1) * local };
      }));
      if (t < 1) animRef.current = requestAnimationFrame(step);
      else setTimeout(() => { setMarkers(baseRef.current); setAnimating(false); }, 600);
    };
    animRef.current = requestAnimationFrame(step);
  };

  const savePlay = async () => {
    if (!playName.trim()) { toast.error('Give your play a name'); return; }
    const body = { name: playName.trim(), phase, board: { markers, paths } };
    try {
      if (currentPlayId) {
        await api.put(`/playbook/plays/${currentPlayId}`, body);
        toast.success('Play updated');
      } else {
        const res = await api.post('/playbook/plays', body);
        setCurrentPlayId(res.data.id);
        toast.success('Play saved');
      }
      loadPlays();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save play'); }
  };

  const loadPlay = (p) => {
    setMarkers(p.board.markers || []);
    setPaths(p.board.paths || {});
    setPlayName(p.name);
    setPhase(p.phase);
    setCurrentPlayId(p.id);
    setSelected(null);
  };

  const deletePlay = async (p) => {
    try {
      await api.delete(`/playbook/plays/${p.id}`);
      toast.success('Play deleted');
      if (p.id === currentPlayId) clearBoard();
      loadPlays();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const color = (team) => (team === 'attack' ? '#A3FF00' : team === 'defense' ? '#ef4444' : '#ffffff');

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="playbook-page">
      <nav className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400" data-testid="playbook-back-button"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <img src="/logo.png" alt="FutureXV" className="h-9" />
            <span className="font-heading font-bold">XV Playbook</span>
            <Badge className="bg-primary-neon/10 text-primary-neon border border-primary-neon/30 text-[10px]">Tactical Board</Badge>
          </div>
          <span className="text-zinc-500 text-sm hidden sm:block">{user?.email}</span>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-[1fr_290px] gap-6">
        <div>
          {/* Toolbar */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button size="sm" onClick={() => addMarker('attack')} className="bg-primary-neon/15 text-primary-neon border border-primary-neon/30 hover:bg-primary-neon/25" data-testid="add-attacker-button"><Plus className="w-3.5 h-3.5 mr-1" />Attacker</Button>
            <Button size="sm" onClick={() => addMarker('defense')} className="bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25" data-testid="add-defender-button"><Plus className="w-3.5 h-3.5 mr-1" />Defender</Button>
            <Button size="sm" onClick={() => addMarker('ball')} variant="outline" className="border-zinc-600 text-zinc-200" data-testid="add-ball-button"><Plus className="w-3.5 h-3.5 mr-1" />Ball</Button>
            <span className="w-px bg-zinc-800 mx-1" />
            <Button size="sm" onClick={() => setMode('move')} variant={mode === 'move' ? 'default' : 'outline'} className={mode === 'move' ? 'bg-primary-neon text-black' : 'border-zinc-700 text-zinc-300'} data-testid="mode-move-button"><MousePointer className="w-3.5 h-3.5 mr-1" />Move</Button>
            <Button size="sm" onClick={() => setMode('path')} variant={mode === 'path' ? 'default' : 'outline'} className={mode === 'path' ? 'bg-primary-neon text-black' : 'border-zinc-700 text-zinc-300'} data-testid="mode-path-button"><Route className="w-3.5 h-3.5 mr-1" />Path</Button>
            <Button size="sm" onClick={animate} className={animating ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'} data-testid="animate-button">{animating ? <><Square className="w-3.5 h-3.5 mr-1" />Stop</> : <><Play className="w-3.5 h-3.5 mr-1" />Run Play</>}</Button>
            <span className="w-px bg-zinc-800 mx-1" />
            <Button size="sm" onClick={clearPaths} variant="outline" className="border-zinc-700 text-zinc-400" data-testid="clear-paths-button"><Eraser className="w-3.5 h-3.5 mr-1" />Clear Lines</Button>
            <Button size="sm" onClick={removeSelected} variant="outline" className="border-zinc-700 text-zinc-400" disabled={!selected} data-testid="remove-marker-button"><Trash2 className="w-3.5 h-3.5 mr-1" />Remove</Button>
          </div>
          {mode === 'path' && <p className="text-amber-400 text-xs mb-2" data-testid="path-mode-hint">Path mode: select a player, then click on the field to add run-line waypoints.</p>}

          {/* Field */}
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full rounded-lg border border-zinc-800 select-none touch-none"
            style={{ background: 'linear-gradient(180deg, #14481f 0%, #196227 50%, #14481f 100%)', cursor: mode === 'path' ? 'crosshair' : 'default' }}
            onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} onClick={onFieldClick} data-testid="playbook-field">
            {/* pitch stripes */}
            {Array.from({ length: 9 }).map((_, i) => (
              <rect key={i} x={(W / 9) * i} y="0" width={W / 9} height={H} fill={i % 2 ? 'rgba(255,255,255,0.03)' : 'transparent'} />
            ))}
            {FIELD_LINES.map((x, i) => (
              <line key={x} x1={x} y1="0" x2={x} y2={H} stroke="rgba(255,255,255,0.55)" strokeWidth={i === 3 ? 3 : 2} strokeDasharray={i === 2 || i === 4 ? '10 8' : 'none'} />
            ))}
            <rect x="0" y="0" width="40" height={H} fill="rgba(163,255,0,0.08)" />
            <rect x={W - 40} y="0" width="40" height={H} fill="rgba(239,68,68,0.08)" />
            {/* run lines */}
            {Object.entries(paths).map(([mid, pts]) => {
              const m = markers.find((x) => x.id === mid);
              if (!m || !pts.length) return null;
              const base = animating && baseRef.current ? baseRef.current.find((x) => x.id === mid) || m : m;
              const d = `M ${base.x} ${base.y} ` + pts.map(([x, y]) => `L ${x} ${y}`).join(' ');
              return (
                <g key={mid}>
                  <path d={d} fill="none" stroke={color(m.team)} strokeWidth="2.5" strokeDasharray="7 6" opacity="0.8" />
                  {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="4" fill={color(m.team)} opacity="0.7" />)}
                </g>
              );
            })}
            {/* markers */}
            {markers.map((m) => (
              <g key={m.id} onPointerDown={(e) => onMarkerDown(e, m)} style={{ cursor: 'grab' }} data-testid={`marker-${m.id}`}>
                {m.team === 'ball' ? (
                  <ellipse cx={m.x} cy={m.y} rx="14" ry="9" fill="#f5f0e6" stroke={selected === m.id ? '#A3FF00' : '#333'} strokeWidth="2.5" transform={`rotate(-20 ${m.x} ${m.y})`} />
                ) : (
                  <>
                    <circle cx={m.x} cy={m.y} r="16" fill={color(m.team)} stroke={selected === m.id ? '#fff' : 'rgba(0,0,0,0.5)'} strokeWidth={selected === m.id ? 3 : 1.5} />
                    <text x={m.x} y={m.y + 5} textAnchor="middle" fontSize="13" fontWeight="800" fill="#000">{m.label}</text>
                  </>
                )}
              </g>
            ))}
          </svg>

          {/* Save bar */}
          <div className="flex flex-wrap gap-2 mt-4 items-center">
            <Input value={playName} onChange={(e) => setPlayName(e.target.value)} placeholder="Play name — e.g. Blindside Switch 22" className="bg-zinc-900 border-zinc-800 text-white max-w-xs h-10" data-testid="play-name-input" />
            <Select value={phase} onValueChange={setPhase}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white w-36 h-10" data-testid="play-phase-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                <SelectItem value="attack">Attack</SelectItem>
                <SelectItem value="defense">Defense</SelectItem>
                <SelectItem value="set_piece">Set Piece</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={savePlay} className="btn-primary" data-testid="save-play-button"><Save className="w-4 h-4 mr-1.5" />{currentPlayId ? 'Update Play' : 'Save Play'}</Button>
            <Button onClick={clearBoard} variant="outline" className="border-zinc-700 text-zinc-400" data-testid="new-play-button">New Board</Button>
          </div>
        </div>

        {/* Saved plays */}
        <Card className="bg-zinc-900 border-zinc-800 p-4 h-fit" data-testid="saved-plays-panel">
          <h3 className="font-heading font-bold mb-3">Saved Plays <Badge className="ml-1 bg-zinc-800 text-zinc-400">{plays.length}</Badge></h3>
          {plays.length === 0 && <p className="text-zinc-600 text-sm">No plays yet — build one and hit Save.</p>}
          <div className="space-y-2 max-h-[540px] overflow-y-auto">
            {plays.map((p) => (
              <div key={p.id} className={`bg-zinc-950 border rounded-lg p-3 ${p.id === currentPlayId ? 'border-primary-neon/50' : 'border-zinc-800'}`} data-testid={`play-item-${p.id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{p.name}</p>
                    <p className="text-zinc-600 text-[11px] capitalize">{p.phase.replace('_', ' ')} · {(p.board.markers || []).length} markers</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 h-7 px-2 text-xs" onClick={() => loadPlay(p)} data-testid={`load-play-${p.id}`}>Load</Button>
                    <Button size="sm" variant="ghost" className="text-red-400 h-7 px-2" onClick={() => deletePlay(p)} data-testid={`delete-play-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
